import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material'
import React, { HTMLAttributes } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useEffectOnce } from 'react-use'

import Icon from 'components/Icon'
import theme from 'theme'
import { Process } from 'types/api/services/service'
import { ServiceFormValues } from 'types/forms'
import { naturalSortBy } from 'utils/list'

export const ServiceProcessSelection: React.FC<{
  processes: Process[]
  hasNextPage: boolean
}> = ({ processes, hasNextPage }) => {
  const { control, getValues, setValue } = useFormContext<ServiceFormValues>()

  const hasOneProcess: boolean = processes.length === 1

  const { processId } = getValues()

  useEffectOnce(() => {
    if (!processId && hasOneProcess) {
      setValue('processId', processes[0].id, { shouldDirty: true })
      setValue('processName', processes[0].name, { shouldDirty: true })
    }
  })

  return (
    <Stack direction="column" gap={4}>
      <FormControl fullWidth={false}>
        <InputLabel required id="process">
          Process
        </InputLabel>
        <Controller
          control={control}
          name="processId"
          render={({ field }) => (
            <Select
              {...field}
              data-test-process-menu
              label="Process"
              labelId="process"
              readOnly={hasOneProcess}
              SelectDisplayProps={
                {
                  'data-test-process-select-label': 'true'
                } as HTMLAttributes<HTMLDivElement>
              }
              onChange={(e, child: React.ReactElement) => {
                setValue(
                  'processName',
                  child?.props?.['data-service-process-name']
                )
                field.onChange(e.target.value, { shouldDirty: true })
              }}
            >
              {naturalSortBy(processes, (p) => p.name).map((p) => (
                <MenuItem
                  key={p.id}
                  data-service-process-name={p.name}
                  data-test-process={p.name}
                  value={p.id}
                >
                  {p.name}
                  {p.characteristics?.port &&
                    ` (port ${p.characteristics?.port})`}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        {hasOneProcess && (
          <FormHelperText>
            <Icon
              css={{
                fontSize: theme.fontSizes[2],
                marginRight: theme.space[2],
                marginTop: theme.space[1]
              }}
              icon="solidCircleInfo"
            />
            Only one process is available
          </FormHelperText>
        )}
        {hasNextPage && (
          <Stack
            css={{ alignItems: 'center', marginTop: theme.space[2] }}
            direction="row"
            gap={1}
          >
            <CircularProgress color="primary" size="1rem" />
            <Typography>Discovering more processes...</Typography>
          </Stack>
        )}
      </FormControl>
    </Stack>
  )
}
